import { convertDateFromUnixToIso, formatDateToCalendarYear } from 'util/formatters/dateFormatter';
import { determineApiPrefix } from 'util/env';
import {
  fieldViewLayerServiceKeys,
  fieldViewLayerServiceKeysInverted,
  fieldViewLayerMappings
} from 'constants/imagery';
import { formatNumberGrouped } from 'util/formatters/numberFormatter';

const buildFieldViewTileUrl = ({ username, uuid, year, crop, endTime, fieldViewLayer, startTime }) => {
  crop = encodeURIComponent(crop);
  const subdomain = determineApiPrefix({ requiresFieldViewDomain: true });
  const baseURL = `https:${subdomain}/tile/v2/${username}/${uuid}/${year}/${crop}/${fieldViewLayer}`;
  const queryString = `startTime=${startTime}&endTime=${endTime}`;
  return `${baseURL}/{z}/{x}/{y}.png?${queryString}`;
};

const formatFieldViewEvent = ({ fieldUse, fieldViewUuid, fieldUuid, username, layer, date }) => {
  const fieldViewLayerServiceKey = fieldViewLayerServiceKeysInverted[layer];
  // formatting that one single map data
  const image = {};
  image.fieldViewLayer = fieldViewLayerMappings[layer];
  image.layer = layer;
  image.fieldUuid = fieldUuid;
  image.fieldViewUuid = fieldViewUuid;
  image.username = username;
  image.year = fieldUse.year;
  image.startTime = fieldUse[`${fieldViewLayerServiceKey}StartDate`];
  image.endTime = fieldUse[`${fieldViewLayerServiceKey}EndDate`];
  image.crop = fieldUse.crop;
  image.cropId = fieldUse.cropId;
  // the below is processed by TileLayer to use the correct values
  image.url = buildFieldViewTileUrl({
    username,
    uuid: image.fieldViewUuid,
    year: image.year,
    crop: image.crop,
    endTime: image.endTime,
    fieldViewLayer: image.fieldViewLayer,
    startTime: image.startTime
  });
  image.date = convertDateFromUnixToIso(date/1000);
  image.displaySeason = `${formatDateToCalendarYear(image.date)} ${fieldUse.crop}`; // TODO new season system, i18n?
  image.overlayUuid = `${image.date}_${image.fieldViewLayer}`;
  return image;
};

const parseFieldViewLayersForDate = ({ layers, fieldUse, fieldViewUuid, fieldUuid, username, date }) => {
  // grabbing all the layers for each event (e.g. yield and combine_speed for a harvest event
  const results = {};
  layers.forEach( (layer) => {
    const image = formatFieldViewEvent({ fieldUse, fieldViewUuid, fieldUuid, username, layer, date });
    if(image.overlayUuid) {
      results[layer] = results[layer] || {};
      results[layer][image.overlayUuid] = image;
    }
  });
  return results;
};

const pullOutEachLayer = ({ fieldUse, fieldViewUuid, fieldUuid, username }) => {
  // looking for potentially multiple events per fieldUse
  const imageLayers = {};
  Object.keys(fieldViewLayerServiceKeys).forEach( (group) => {
    let date = fieldUse[`${group}Date`];

    if(!date) {
      return;
    }

    const layersForFieldOnDate = parseFieldViewLayersForDate({
      layers: fieldViewLayerServiceKeys[group],
      fieldUse,
      fieldViewUuid,
      fieldUuid,
      username,
      date
    });
    Object.keys(layersForFieldOnDate).forEach( (layerType) => {
      imageLayers[layerType] = layersForFieldOnDate[layerType];
    });
  });
  return imageLayers;
};

const parseFieldViewField = ({ field, username }) => {
  // Parses a single "field" listing, which can have multiple fieldUses (e.g. a corn crop, then a soy crop, etc)
  let fieldResults = {};
  const fieldViewUuid = field.cloudUUID;
  const fieldUuid = fieldViewUuid.toLowerCase(); // SERVICES, WHY YOU NOT CONSISTENT?
  field.yearCropInfoList.forEach( (fieldUse) => {
    if ( !fieldUse.cropId ) {
      return;
    }
    // seperate each event from this crop/fieldUse (planting, harvest, liquid)
    const layersForFieldUse = pullOutEachLayer({ fieldUse, fieldViewUuid, fieldUuid, username });
    Object.keys(layersForFieldUse).forEach( (layerType) => {
      Object.keys(layersForFieldUse[layerType]).forEach( (eventDate) => {
        fieldResults[layerType] = {
          ...fieldResults[layerType],
          [eventDate]: { ...layersForFieldUse[layerType][eventDate] }
        };
      });
    });
  });
  return { [fieldUuid]: fieldResults };
};

export const parseFieldViewImagery = (fieldViewData) => {
  let results = {};
  if( !fieldViewData.fields || !fieldViewData.fields[0] ) {
    throw new Error('fieldview call had no data');
  }
  const fields = fieldViewData.fields[0].fields; // Why?!
  const username = fieldViewData.fields[0].email; // Fieldview uses email address
  fields.forEach( (field) => {
    if (field.yearCropInfoList.length > 0) {
      const layerDetails = parseFieldViewField({ field, username });
      results = { ...results, ...layerDetails };
    }
  });
  return results;
};

export const parseFieldViewLegend = ({ rawLegend, layer }) => {
  const legend = {
    labelKey: `label-text__overlay-legend--${layer}`,
    layer,
    colors: [],
    displayValues: [],
    rawValues: []
  };
  rawLegend.forEach( (color) => {
    legend.colors.push( { r: color.r, b: color.b, g: color.g } );
    legend.displayValues.push( color.stringValue || formatNumberGrouped(color.value.toFixed(0)) );
    legend.rawValues.push( color.stringValue || color.value );
  });
  return legend;
};
